import { useState } from 'react';

import type { LancarFeriadoInput, MeuFeriado } from '@/api/meusFeriados';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { useNotification } from '@/context/NotificationProvider';
import type { useLancarFeriado } from '@/hooks/useMeusFeriados';
import { classifyError, getUserVisibleErrorMessage, notifyMutationError } from '@/lib/errors';

/** O pedaço do lançamento que diz onde vale. */
export type LugarDoLancamento = Pick<LancarFeriadoInput, 'abrangencia' | 'tribunal' | 'municipio_ibge' | 'uf' | 'justica'>;

/** Um lugar que a pessoa pode escolher: o rótulo na tela e o que vai ao backend. */
export interface LugarDoFormulario {
  value: string;
  label: string;
  /** O que a busca do Combobox olha (nome, sigla, UF), sem a contagem do rótulo. */
  searchText?: string;
  lugar: LugarDoLancamento;
}

interface Props {
  lugares: LugarDoFormulario[];
  /** O lugar já escolhido ao abrir; vazio obriga a escolher. */
  lugarInicial?: string;
  /** Lista longa (o acervo): escolhe com busca em vez do select. */
  buscarLugar?: boolean;
  /** A mutation é de quem abre: o diálogo não fecha com o envio em curso. */
  lancar: ReturnType<typeof useLancarFeriado>;
  onLancado: (regra: MeuFeriado) => void;
  onCancelar: () => void;
}

/**
 * Os campos do feriado ou da suspensão que o advogado lança (PLANO_CONTAGEM_PRAZOS, Bloco E) —
 * o mesmo formulário na conta de um prazo (os lugares do ato) e na página de Feriados (os lugares
 * do acervo, docs/PLANO_TELAS_TAGS_FERIADOS.md). Só a origem dos lugares muda.
 */
export function FeriadoForm({ lugares, lugarInicial = '', buscarLugar = false, lancar, onLancado, onCancelar }: Props) {
  const { showSuccess, showError } = useNotification();
  const [onde, setOnde] = useState(lugarInicial);
  const [tipo, setTipo] = useState<LancarFeriadoInput['tipo']>('suspensao');
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const [nome, setNome] = useState('');
  const [ato, setAto] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const escolhido = lugares.find((l) => l.value === onde);
    const faltando = !escolhido
      ? 'Escolha onde vale.'
      : !de
        ? 'Informe o dia.'
        : ate && ate < de
          ? 'O fim é anterior ao início.'
          : !nome.trim()
            ? 'Dê um nome (ex.: PJe fora do ar).'
            : !ato.trim()
              ? 'Informe o ato, a portaria ou o aviso do tribunal.'
              : null;
    setErro(faltando);
    if (faltando || !escolhido) return;
    lancar.mutate(
      {
        nome: nome.trim(),
        tipo,
        data: de,
        data_fim: ate || null,
        ...escolhido.lugar,
        ato_normativo: ato.trim(),
        observacao: null,
      },
      {
        onSuccess: (regra) => {
          showSuccess('Lançado. Os vencimentos foram refeitos.');
          onLancado(regra);
        },
        onError: (err) => {
          // 422 (dado recusado) e 403 (sem permissão nesta conta) são do formulário.
          const status = classifyError(err).status;
          if (status === 403) {
            setErro('Sem permissão para lançar nesta conta.');
            return;
          }
          if (status === 422) {
            setErro(getUserVisibleErrorMessage(err));
            return;
          }
          notifyMutationError(err, showError);
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      <div className="space-y-2">
        <Label htmlFor="lancar-tipo">O que houve</Label>
        <Select value={tipo} onValueChange={(v) => setTipo(v as LancarFeriadoInput['tipo'])}>
          <SelectTrigger id="lancar-tipo" className="h-11 sm:h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="suspensao">Suspensão de prazos (ex.: sistema fora do ar)</SelectItem>
            <SelectItem value="feriado">Feriado ou fórum fechado</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="lancar-de">Dia</Label>
          <Input id="lancar-de" type="date" className="h-11 sm:h-9" value={de} onChange={(e) => setDe(e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="lancar-ate">Até (se for período)</Label>
          <Input id="lancar-ate" type="date" className="h-11 sm:h-9" value={ate} onChange={(e) => setAte(e.target.value)} />
        </div>
      </div>

      <div className="space-y-2">
        {buscarLugar ? (
          <>
            <Label id="lancar-onde-rotulo">Onde vale</Label>
            <div role="group" aria-labelledby="lancar-onde-rotulo">
              <Combobox
                value={onde}
                onValueChange={setOnde}
                options={lugares}
                placeholder="Escolha um lugar do acervo"
                searchPlaceholder="Buscar tribunal, comarca ou estado"
                emptyMessage="Nenhum lugar do acervo com esse nome."
                className="min-h-11 sm:min-h-9"
              />
            </div>
          </>
        ) : (
          <>
            <Label htmlFor="lancar-onde">Onde vale</Label>
            <Select value={onde} onValueChange={setOnde}>
              <SelectTrigger id="lancar-onde" className="h-11 sm:h-9">
                <SelectValue placeholder="Escolha" />
              </SelectTrigger>
              <SelectContent>
                {lugares.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="lancar-nome">Nome</Label>
        <Input
          id="lancar-nome"
          className="h-11 sm:h-9"
          placeholder="Ex.: PJe fora do ar"
          maxLength={200}
          value={nome}
          onChange={(e) => setNome(e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="lancar-ato">Ato do tribunal</Label>
        <Input
          id="lancar-ato"
          className="h-11 sm:h-9"
          placeholder="Portaria, ato ou aviso — com número e data"
          maxLength={300}
          value={ato}
          onChange={(e) => setAto(e.target.value)}
        />
      </div>

      {erro && (
        <Alert variant="destructive">
          <AlertDescription>{erro}</AlertDescription>
        </Alert>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancelar} disabled={lancar.isPending} className="h-11 sm:h-9">
          Cancelar
        </Button>
        <Button type="submit" disabled={lancar.isPending} className="h-11 sm:h-9">
          {lancar.isPending && <Spinner className="mr-2 text-inherit" />}
          Lançar
        </Button>
      </DialogFooter>
    </form>
  );
}
